import dayjs from 'dayjs'
import {FormikProps} from 'formik'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {DateObject} from 'react-multi-date-picker'
import {GChip, GeneratedFormProps, GIconButton} from 'src/app/components/Libs'
import {TableHeaders} from 'src/app/components/Libs/Table/GTable'
import {DateRangePicker2} from 'src/app/components/Widgets/DateRangePicker2'
import OutsideSelectFilterLocal from 'src/app/components/Widgets/OutsideSelect/OutsideSelectFilterLocal'
import {useGlobalParams} from 'src/app/hooks/global-params.hook'
import {usePermissions} from 'src/app/hooks/permissions.hook'
import {EPermissions, TTableDefault} from 'src/app/models'
import {IApproval, IApprovalQuery} from 'src/app/models/Job/Approval.model'
import {cn} from 'src/app/utils/cn.utils'
import {ChipPriorityJob} from '../components/ChipJob'

export const useTableApproval = (
  state: TTableDefault<IApprovalQuery>,
  formik: FormikProps<any>,
  page: 'WAITING' | 'DONE'
) => {
  const {hasAccess} = usePermissions()

  const {TASK_PRIORITY: taskPriorityResult} = useGlobalParams()

  const [selected, setSelected] = useState<IApproval>()
  const [deleteModal, setDeleteModal] = useState<boolean>(false)
  const [rejectModal, setRejectModal] = useState(false)
  const [approveModal, setApproveModal] = useState(false)
  const [dateRange, setDateRange] = useState<any>([])

  const handleDateSubmit = useCallback(() => {
    const now = dayjs()

    const start = dayjs(dateRange[0] || now)
    const end = dayjs(dateRange[1] || now)

    const [finalStart, finalEnd] = end.isBefore(start) ? [end, start] : [start, end]

    formik.setFieldValue('start_date', finalStart.toISOString())
    formik.setFieldValue('end_date', finalEnd.toISOString())
  }, [dateRange, formik])

  const handleDateCancel = useCallback(() => {
    formik.setFieldValue('start_date', undefined)
    formik.setFieldValue('end_date', undefined)
    setDateRange([])
  }, [formik])

  const handleDateClose = useCallback(() => {
    if (formik.values.start_date && formik.values.end_date) {
      return
    } else {
      formik.setFieldValue('start_date', undefined)
      formik.setFieldValue('end_date', undefined)
      setDateRange([])
    }
  }, [formik])

  useEffect(() => {
    if (formik.values.start_date && formik.values.end_date) {
      const start = new DateObject(dayjs(formik?.values?.start_date).toDate())
      const end = new DateObject(dayjs(formik?.values?.end_date).toDate())

      setDateRange([start, end])
    } else {
      setDateRange([])
    }

    //eslint-disable-next-line
  }, [formik.values.start_date, formik?.values?.end_date])

  const headers: TableHeaders<IApproval>[] = useMemo(
    () => [
      {
        value: 'number',
        headerName: 'No',
        renderCell: (params, index) => state.page * (state.size || 10) + index + 1,
        width: 57,
      },
      {
        value: 'job_id',
        headerName: 'ID Tugas',
        sort: true,
        renderCell: (params) => (
          <span
            className='underline cursor-pointer text-primary-500'
            onClick={(e) => {
              e.stopPropagation()
              setSelected(params)
              window.location.hash = `${params.id},${params.agent_id}`
            }}
            role='button'
          >
            {params.job_id}
          </span>
        ),
        width: 200,
      },
      {value: 'name', headerName: 'Nama Tugas', sort: true, width: 300},
      {
        value: 'priority',
        headerName: 'Urgensi',
        renderCell: (params) => <ChipPriorityJob status={params.priority} />,
        width: 120,
      },
      {
        value: 'agent_name',
        sort: true,
        headerName: 'Diserahkan',
        renderCell: (params) => (
          <span>
            <div>{params.agent_name}</div>
            <div>
              {params.completed_at ? dayjs(params.completed_at).format('DD MMMM YYYY HH:mm') : '-'}
            </div>
          </span>
        ),
        width: 250,
      },
      {
        value: 'is_overdue',
        headerName: 'Status Waktu',
        renderCell: (params) => (
          <GChip variant={params.is_overdue ? 'danger' : 'success'}>
            {params.is_overdue ? 'Terlambat' : 'Tepat Waktu'}
          </GChip>
        ),
        width: 155,
      },
      {
        value: 'is_approved',
        headerName: 'Status Persetujuan',
        renderCell: (params) => (
          <GChip variant={'neutral'}>{params.is_approved ? 'Disetujui' : 'Ditolak'}</GChip>
        ),
        width: 155,
        className: cn({hidden: page === 'WAITING'}),
      },
      {
        value: 'action',
        headerName: 'Aksi',
        width: 120,
        className: cn('sticky right-0 z-[1] drop-shadow-[0_3px_5px_rgba(16,24,40,0.1)]', {
          hidden: page === 'DONE',
        }),
        bodyClassname: 'bg-white',
        renderCell: (params) => {
          return (
            <div
              className='flex gap-2 w-[120px]'
              onClick={(e) => e.stopPropagation()}
              role='button'
            >
              {hasAccess(EPermissions.JOB_APPROVAL_UPDATE) && (
                <GIconButton
                  data-tooltip-id='reject-tugas-tooltip'
                  icon='IconDocumentCross'
                  onClick={() => {
                    setRejectModal(true)
                    setSelected(params)
                  }}
                />
              )}

              {hasAccess(EPermissions.JOB_APPROVAL_UPDATE) && (
                <GIconButton
                  data-tooltip-id='approve-tugas-tooltip'
                  icon='IconDocumentCheck'
                  onClick={() => {
                    setApproveModal(true)
                    setSelected(params)
                  }}
                />
              )}
            </div>
          )
        },
      },
    ],
    [state.size, state.page, hasAccess, page]
  )

  const formFilter: GeneratedFormProps[] = useMemo(
    () => [
      {
        titleclassname: 'm-0',
        renderForm: (
          <OutsideSelectFilterLocal
            title={'Urgensi'}
            options={taskPriorityResult}
            selected={formik.values.priorities}
            setSelected={(v: any) => formik.setFieldValue('priorities', v)}
            defaultValue={state.priorities ?? []}
          />
        ),
      },
      {
        title: 'Status Waktu',
        value: 'is_overdue',
        type: 'radio',
        options: [
          {value: false, label: 'Tepat Waktu'},
          {value: true, label: 'Terlambat'},
        ],
      },
      {
        title: 'Tanggal Diserahkan',
        titleclassname: 'mt-0',
        renderForm: (
          <DateRangePicker2
            range
            startDate={dateRange[0] ? dayjs(dateRange[0]).toISOString() : undefined}
            endDate={dateRange[1] ? dayjs(dateRange[1]).toISOString() : undefined}
            onChange={(v) => setDateRange(v)}
            onCancelValue={handleDateCancel}
            onSubmitValue={handleDateSubmit}
            onClose={handleDateClose}
            value={dateRange}
            placeholder='Pilih rentang tanggal'
          />
        ),
      },
    ],
    [
      formik,
      state,
      taskPriorityResult,
      dateRange,
      handleDateSubmit,
      handleDateCancel,
      handleDateClose,
    ]
  )

  return {
    headers,
    deleteModal,
    setDeleteModal,
    selected,
    setSelected,
    formFilter,
    rejectModal,
    setRejectModal,
    approveModal,
    setApproveModal,
  }
}
